import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Icon,
  Box,
  HStack,
  Flex,
  useColorModeValue,
  Badge,
  Alert,
  AlertIcon,
  ModalCloseButton,
  Divider,
} from '@chakra-ui/react';
import {
  FaCcVisa,
  FaCcMastercard,
  FaCcAmex,
  FaCcDiscover,
  FaCreditCard,
  FaTrash,
} from 'react-icons/fa6';
import StripeLinkLogo from '../../../common/StripeLinkLogo';

const RemovePaymentMethodModal = ({
  isOpen,
  onClose,
  paymentMethod,
  onConfirm,
  isLoading = false,
}) => {
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const bgColorHighlight = useColorModeValue('red.50', 'red.900');

  const getCardIcon = (brand) => {
    const brands = {
      visa: FaCcVisa,
      mastercard: FaCcMastercard,
      amex: FaCcAmex,
      discover: FaCcDiscover,
    };

    return brands[brand?.toLowerCase()] || FaCreditCard;
  };

  const handleConfirm = () => {
    onConfirm(paymentMethod.id);
  };

  if (!paymentMethod) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent pt={8} pb={6} px={6}>
        <ModalCloseButton />
        <Text textStyle="heading4" textAlign="center">
          Remove Payment Method
        </Text>
        <Divider my="10px" />
        <ModalBody>
          <Text textStyle="bodyMedium" mb={4}>
            Are you sure you want to remove this payment method?
          </Text>

          {paymentMethod.is_default && (
            <Alert status="warning" mb={4} borderRadius="md">
              <AlertIcon />
              <Text textStyle="bodySmall">
                This is your default payment method. If removed, you will need
                to add and set a new default payment method for future
                transactions.
              </Text>
            </Alert>
          )}

          {/* Payment method preview */}
          <Box
            p={4}
            borderWidth="1px"
            borderRadius="md"
            borderColor={borderColor}
            bg={bgColorHighlight}
            mb={4}
          >
            <Flex align="center">
              {paymentMethod.type !== 'link' && (
                <Icon
                  as={getCardIcon(
                    paymentMethod.card?.brand || paymentMethod.brand
                  )}
                  boxSize="24px"
                  mr={3}
                  color="gray.500"
                />
              )}
              <Box>
                <HStack>
                  {paymentMethod.type === 'link' ? (
                    <Box color="gray.800">
                      <StripeLinkLogo height="20px" />
                    </Box>
                  ) : (
                    <Text fontWeight="medium">
                      {paymentMethod.card?.brand ||
                        paymentMethod.brand ||
                        'Card'}{' '}
                      •••• {paymentMethod.card?.last4 || paymentMethod.last4}
                    </Text>
                  )}
                  {paymentMethod.is_default && (
                    <Badge colorScheme="blue" variant="subtle">
                      Default
                    </Badge>
                  )}
                </HStack>
                {paymentMethod.type === 'link'
                  ? paymentMethod.email && (
                      <Text fontSize="sm" color="gray.600">
                        {paymentMethod.email}
                      </Text>
                    )
                  : (paymentMethod.card?.exp_month ||
                      paymentMethod.exp_month) && (
                      <Text fontSize="sm" color="gray.600">
                        Expires{' '}
                        {paymentMethod.card?.exp_month ||
                          paymentMethod.exp_month}
                        /
                        {paymentMethod.card?.exp_year || paymentMethod.exp_year}
                      </Text>
                    )}
              </Box>
            </Flex>
          </Box>
        </ModalBody>

        <ModalFooter>
          <Flex direction="column" w="100%" gap={4}>
            <Button
              colorScheme="red"
              onClick={handleConfirm}
              isLoading={isLoading}
              leftIcon={<Icon as={FaTrash} />}
              w="100%"
            >
              Remove
            </Button>
            <Button variant="link" onClick={onClose} isDisabled={isLoading}>
              <Text textStyle="bodySmall">Cancel</Text>
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RemovePaymentMethodModal;
